import React, { createContext, useContext, useState, useEffect } from 'react';
import { useDailyStreak } from '@/lib/hooks/useLocalStorage';

const FavoritesContext = createContext(null);

// Asks the service worker to fetch+cache (or evict) a saved page's real URL,
// independent of whether the browser ever visits it - see sw.js's message
// listener. Silently a no-op with no controller (no SW yet, or offline on
// first load), which just means that save doesn't get offline coverage.
function postToServiceWorker(message) {
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.controller?.postMessage(message);
}

// `description` is what you did, past tense, shown once the badge is earned.
// `hint` is what to do, shown while it is still locked - restating the goal in
// past tense reads as though you have already done it. `target`/`current` drive
// a real progress readout, so a locked badge can say how far along you are
// rather than just what the goal is.
export const ACHIEVEMENTS = [
  { id: 'first-fact', emoji: '❤️', title: 'First Fact', description: 'Saved your first fact', hint: 'Tap the heart on any fact to save it to your Pack.', target: 1, current: (s) => s.favoritesCount, unit: 'fact saved', check: (s) => s.favoritesCount >= 1 },
  { id: 'fact-collector', emoji: '📚', title: 'Fact Collector', description: 'Saved 10 facts', hint: 'Save 10 facts to your Pack.', target: 10, current: (s) => s.favoritesCount, unit: 'facts saved', check: (s) => s.favoritesCount >= 10 },
  { id: 'fact-fanatic', emoji: '🏆', title: 'Fact Fanatic', description: 'Saved 25 facts', hint: 'Save 25 facts to your Pack.', target: 25, current: (s) => s.favoritesCount, unit: 'facts saved', check: (s) => s.favoritesCount >= 25 },
  { id: 'quiz-taker', emoji: '🧩', title: 'Quiz Taker', description: 'Completed your first quiz', hint: 'Finish any quiz, including the Test Yourself quiz on an encyclopedia animal.', target: 1, current: (s) => s.quizzesCompleted, unit: 'quiz completed', check: (s) => s.quizzesCompleted >= 1 },
  { id: 'quiz-explorer', emoji: '🔎', title: 'Quiz Explorer', description: 'Completed 5 quizzes', hint: 'Finish 5 quizzes.', target: 5, current: (s) => s.quizzesCompleted, unit: 'quizzes completed', check: (s) => s.quizzesCompleted >= 5 },
  { id: 'quiz-master', emoji: '🎓', title: 'Quiz Master', description: 'Completed 10 quizzes', hint: 'Finish 10 quizzes.', target: 10, current: (s) => s.quizzesCompleted, unit: 'quizzes completed', check: (s) => s.quizzesCompleted >= 10 },
  { id: 'streak-3', emoji: '🔥', title: 'On a Roll', description: '3-day visit streak', hint: 'Visit Beastly Facts on 3 days in a row.', target: 3, current: (s) => s.streakCount, unit: 'day streak', check: (s) => s.streakCount >= 3 },
  { id: 'streak-7', emoji: '⚡', title: 'Week-Long Wildling', description: '7-day visit streak', hint: 'Visit Beastly Facts on 7 days in a row.', target: 7, current: (s) => s.streakCount, unit: 'day streak', check: (s) => s.streakCount >= 7 },
  // Beastle streaks count solved days in a row, and read the best streak ever
  // reached, so a badge stays earned after a missed day resets the live one.
  { id: 'beastle-7', emoji: '🔤', title: 'Beastle Week', description: 'Solved Beastle 7 days in a row', hint: 'Solve the daily Beastle 7 days in a row.', target: 7, current: (s) => s.beastleBest, unit: 'day Beastle streak', check: (s) => s.beastleBest >= 7 },
  { id: 'beastle-30', emoji: '🦉', title: 'Beastle Month', description: 'Solved Beastle 30 days in a row', hint: 'Solve the daily Beastle 30 days in a row.', target: 30, current: (s) => s.beastleBest, unit: 'day Beastle streak', check: (s) => s.beastleBest >= 30 },
  { id: 'beastle-100', emoji: '👑', title: 'Beastle Legend', description: 'Solved Beastle 100 days in a row', hint: 'Solve the daily Beastle 100 days in a row.', target: 100, current: (s) => s.beastleBest, unit: 'day Beastle streak', check: (s) => s.beastleBest >= 100 },
];

export function FavoritesProvider({ children }) {
  // Single source of truth for the visit streak - Navbar reads it from this
  // context instead of calling useDailyStreak() a second time, since two
  // independent hook instances would race writing the same localStorage key.
  const { streak, recordVisit, loaded: streakLoaded } = useDailyStreak();
  // Waits for the real stored streak to load first (see recordVisit's own
  // guard) - firing this on every mount regardless would see the default
  // {count:0, lastDate:null} and reset every returning visitor's streak to 1.
  // Also skipped entirely during prerendering (prerender.mjs sets this flag) -
  // recordVisit() always WRITES a new value even starting from "no prior
  // visit", and its timing relative to prerender.mjs's own capture-readiness
  // wait is an unrelated race; skipping it keeps captured HTML pinned to the
  // same default state every real client's first hydration pass also starts
  // from, rather than racing to sometimes bake the post-write value in.
  useEffect(() => {
    if (streakLoaded && !window.__IS_PRERENDER__) recordVisit();
  }, [streakLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // These three all start at their empty/zero default on every render (server
  // or client) and pick up the real stored value in an effect after mount -
  // see useLocalStorage.js's comment for why: prerender.mjs's puppeteer
  // profile always has empty localStorage, so a returning visitor's real
  // favorites/quiz history would otherwise differ from the server's default
  // on the very first client render, a structural hydration mismatch (these
  // feed unlockedAchievements/currentAchievementToast, which AchievementToast
  // renders above AppLayout's Suspense boundary) severe enough to make React
  // discard and re-render the whole page client-side instead of hydrating it.
  const [favorites, setFavorites] = useState([]);
  const [savedContent, setSavedContent] = useState([]);
  const [savedQuizResults, setSavedQuizResults] = useState([]);
  const [quizzesCompleted, setQuizzesCompleted] = useState(0);
  const [beastleBest, setBeastleBest] = useState(0);
  const isFirstFavoritesWrite = React.useRef(true);
  const isFirstSavedContentWrite = React.useRef(true);
  const isFirstQuizResultsWrite = React.useRef(true);

  useEffect(() => {
    try {
      const item = localStorage.getItem('beastly-favorites');
      if (item !== null) setFavorites(JSON.parse(item));
    } catch { /* ignore */ }
  }, []);

  // Separate store from `favorites` on purpose: favorites is a flat array of
  // numeric fact ids, keyed and consumed all over the app (FactCard, Pack,
  // achievements' favoritesCount). Guides/encyclopedia/beastlypedia/articles
  // are keyed by string slug instead, so they get their own store rather than
  // overloading the fact-id shape.
  useEffect(() => {
    try {
      const item = localStorage.getItem('beastly-saved-content');
      if (item !== null) setSavedContent(JSON.parse(item));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    try {
      const item = localStorage.getItem('beastly-quiz-results');
      if (item !== null) setSavedQuizResults(JSON.parse(item));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    try {
      const stored = parseInt(localStorage.getItem('beastly-quizzes-completed') || '0', 10) || 0;
      if (stored > 0) setQuizzesCompleted(stored);
    } catch { /* ignore */ }
  }, []);

  // Beastle owns its stats (src/lib/beastle/day.js STORAGE.stats); this only
  // mirrors the best streak for the achievements, and the game reports a new
  // one through recordBeastleStreak when a daily is solved.
  useEffect(() => {
    try {
      const stats = JSON.parse(localStorage.getItem('beastle-stats') || 'null');
      if (stats?.maxStreak > 0) setBeastleBest(stats.maxStreak);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (isFirstFavoritesWrite.current) {
      isFirstFavoritesWrite.current = false;
      return;
    }
    try { localStorage.setItem('beastly-favorites', JSON.stringify(favorites)); } catch {}
  }, [favorites]);

  useEffect(() => {
    if (isFirstSavedContentWrite.current) {
      isFirstSavedContentWrite.current = false;
      return;
    }
    try { localStorage.setItem('beastly-saved-content', JSON.stringify(savedContent)); } catch {}
  }, [savedContent]);

  useEffect(() => {
    if (isFirstQuizResultsWrite.current) {
      isFirstQuizResultsWrite.current = false;
      return;
    }
    try {
      localStorage.setItem('beastly-quiz-results', JSON.stringify(savedQuizResults));
    } catch {
      // The list is no longer capped, so it can in principle outgrow the
      // storage quota. A bare catch here would mean every save from that point
      // on silently did nothing, which is worse than losing the oldest few:
      // the user would keep pressing Save to Pack and keep seeing it confirm.
      // Drop the oldest results until it fits, then reflect that back into
      // state so what is on screen matches what actually persisted.
      let trimmed = savedQuizResults;
      while (trimmed.length > 1) {
        trimmed = trimmed.slice(0, Math.floor(trimmed.length / 2));
        try {
          localStorage.setItem('beastly-quiz-results', JSON.stringify(trimmed));
          setSavedQuizResults(trimmed);
          return;
        } catch {
          // still too big - halve again
        }
      }
    }
  }, [savedQuizResults]);

  const toggleFavorite = (factId) => {
    setFavorites(prev =>
      prev.includes(factId) ? prev.filter(id => id !== factId) : [...prev, factId]
    );
  };

  const isFavorite = (factId) => favorites.includes(factId);

  // item: { type: 'guide' | 'encyclopedia' | 'beastlypedia' | 'article', id, title, subtitle, url }
  // The display fields are captured at save time rather than re-derived from
  // id later - guide/encyclopedia/beastlypedia/article data each live in
  // differently-shaped modules, and the saving page already has everything
  // needed on hand, so there is no single lookup Pack could reuse instead.
  const toggleSavedContent = (item) => {
    setSavedContent(prev => {
      const already = prev.some(s => s.type === item.type && s.id === item.id);
      if (already) {
        postToServiceWorker({ type: 'UNCACHE_SAVED_PAGE', url: item.url });
        return prev.filter(s => !(s.type === item.type && s.id === item.id));
      }
      postToServiceWorker({ type: 'CACHE_SAVED_PAGE', url: item.url });
      return [...prev, { ...item, savedAt: new Date().toISOString() }];
    });
  };

  const isContentSaved = (type, id) => savedContent.some(s => s.type === type && s.id === id);

  const removeSavedContent = (type, id) => {
    setSavedContent(prev => {
      const item = prev.find(s => s.type === type && s.id === id);
      if (item) postToServiceWorker({ type: 'UNCACHE_SAVED_PAGE', url: item.url });
      return prev.filter(s => !(s.type === type && s.id === id));
    });
  };

  const clearFavorites = () => {
    setFavorites([]);
    localStorage.removeItem('beastly-favorites');
  };

  const saveQuizResult = (quizResult) => {
    const newResult = {
      ...quizResult,
      savedAt: new Date().toISOString(),
      id: `quiz-${Date.now()}`
    };
    // Deliberately uncapped. Animal-quiz cards are naturally bounded by the
    // number of encyclopedia animals (retaking one replaces its card rather
    // than adding a second), and losing a card someone earned to make room for
    // a newer one is the wrong trade. The persistence effect above handles the
    // storage quota if a collection ever does get large enough to hit it.
    setSavedQuizResults(prev => [newResult, ...prev]);
  };

  const removeQuizResult = (id) => {
    setSavedQuizResults(prev => prev.filter(r => r.id !== id));
  };

  // Call once per quiz completion (personality result shown, trivia/knowledge
  // finished, per-animal quiz finished) - independent of whether the user
  // also chooses to save the result to their Pack.
  const recordQuizCompletion = () => {
    setQuizzesCompleted(prev => {
      const next = prev + 1;
      try { localStorage.setItem('beastly-quizzes-completed', String(next)); } catch {}
      return next;
    });
  };

  const recordBeastleStreak = (n) => {
    setBeastleBest(prev => Math.max(prev, n));
  };

  const achievementState = { favoritesCount: favorites.length, quizzesCompleted, streakCount: streak, beastleBest };
  const unlockedAchievements = ACHIEVEMENTS.filter(a => a.check(achievementState));

  // Seeded once, on first-ever load, with whatever's already unlocked at that
  // moment - so returning users aren't hit with a pile of toasts for things
  // they achieved before this feature existed. Deliberately NOT effect-driven:
  // an effect here raced with React 18 StrictMode's dev-only double-invoke
  // (the localStorage write leaked into a second "fresh" mount that then read
  // it back and thought everything was already seen). Pending toasts are just
  // unlockedAchievements minus seenAchievements, computed straight in render;
  // seenAchievements is only ever mutated by the explicit dismiss below.
  const [seenAchievements, setSeenAchievements] = useState(() => {
    try {
      const raw = localStorage.getItem('beastly-seen-achievements');
      if (raw !== null) return JSON.parse(raw);
    } catch {}
    const seed = ACHIEVEMENTS.filter(a => a.check(achievementState)).map(a => a.id);
    try { localStorage.setItem('beastly-seen-achievements', JSON.stringify(seed)); } catch {}
    return seed;
  });

  const currentAchievementToast = unlockedAchievements.find(a => !seenAchievements.includes(a.id)) || null;

  const dismissCurrentToast = () => {
    if (!currentAchievementToast) return;
    setSeenAchievements(prev => {
      if (prev.includes(currentAchievementToast.id)) return prev;
      const next = [...prev, currentAchievementToast.id];
      try { localStorage.setItem('beastly-seen-achievements', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  return (
    <FavoritesContext.Provider value={{
      favorites,
      toggleFavorite,
      isFavorite,
      clearFavorites,
      savedContent,
      toggleSavedContent,
      isContentSaved,
      removeSavedContent,
      savedQuizResults,
      saveQuizResult,
      removeQuizResult,
      quizzesCompleted,
      recordQuizCompletion,
      recordBeastleStreak,
      streak,
      unlockedAchievements,
      // Exposed so the Pack can show how far along a locked badge is, rather
      // than only whether it is earned.
      achievementState,
      currentAchievementToast,
      dismissCurrentToast,
    }}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavoritesCtx() {
  return useContext(FavoritesContext);
}
